/**
 * PlanFinder — interaktywny konfigurator planu na górze zakładki Billing.
 * Bierze pod uwagę WSZYSTKIE verticale (Lead Generation / E-commerce / Organic)
 * z tej samej matrycy co karty planów (VERTICALS). User zaznacza cele + potrzeby,
 * a karta po prawej rekomenduje konkretny plan (vertical + tier) i odpala checkout.
 */
import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { VERTICALS } from "@/components/billing-panel";
import {
  Zap,
  ShoppingCart,
  Film,
  Check,
  Sparkles,
  Users,
  Coins,
  FileText,
  Image as ImageIcon,
  LayoutGrid,
  Layers,
  Megaphone,
} from "lucide-react";

type Vertical = (typeof VERTICALS)[number]["id"];
type Tier = "start" | "pro" | "expert";

const fmt = (n: number) => n.toLocaleString("pl-PL");
const findVertical = (id: Vertical) => VERTICALS.find((v) => v.id === id) ?? VERTICALS[0];
const TIER_ORDER: Tier[] = ["start", "pro", "expert"];

// Cele → vertical. Priorytet rekomendacji gdy zaznaczono kilka.
const GOALS: { key: Vertical; label: string; icon: React.ReactNode; priority: number }[] = [
  {
    key: "lead_generation",
    label: "Reklamy i pozyskiwanie leadów",
    icon: <Zap className="w-4 h-4" />,
    priority: 3,
  },
  {
    key: "ecommerce",
    label: "Sprzedaż w e-commerce",
    icon: <ShoppingCart className="w-4 h-4" />,
    priority: 2,
  },
  {
    key: "content_creation",
    label: "Content organiczny (reelsy, grafiki, Inspiracje)",
    icon: <Film className="w-4 h-4" />,
    priority: 1,
  },
];

// Potrzeby podbijające tier (ignorowane gdy vertical nie ma danego tieru, np. Organic).
const NEEDS: { key: string; label: string; tier: Tier }[] = [
  { key: "crm", label: "CRM / integracja (LeadConnector, HubSpot)", tier: "pro" },
  { key: "analyst", label: "Analityk AI z rekomendacjami", tier: "pro" },
  { key: "tracking", label: "Zaawansowany tracking + CAPI", tier: "expert" },
  { key: "agency", label: "Panel agencyjny / wielu klientów", tier: "expert" },
];

// Koszt tokenów per typ treści (app tokens). Bazuje na realnej tokenomii:
// grafika ≈ 55, karuzela ≈ 5×grafika, video AI ≈ Seedance 8s.
const TOKEN_SLIDER = { min: 900, max: 18000, step: 100 };

// Paczki doładowań (Stripe) — do wyliczenia dopłaty gdy slider > tokeny planu.
const TOKEN_PACKS = [
  { n: 4000, zl: 179 },
  { n: 1500, zl: 79 },
  { n: 500, zl: 29 },
];

function topupCost(extraTokens: number): number {
  if (extraTokens <= 0) return 0;
  let remaining = extraTokens;
  let cost = 0;
  for (const pack of TOKEN_PACKS) {
    while (remaining >= pack.n) {
      cost += pack.zl;
      remaining -= pack.n;
    }
  }
  if (remaining > 0) cost += TOKEN_PACKS[TOKEN_PACKS.length - 1].zl; // reszta → najmniejsza paczka
  return cost;
}

const CONTENT_COSTS: {
  key: string;
  label: string;
  icon: React.ReactNode;
  cost: number;
  paidOnly?: boolean;
}[] = [
  { key: "posty", label: "Posty", icon: <FileText className="w-4 h-4" />, cost: 40 },
  { key: "grafiki", label: "Grafiki", icon: <ImageIcon className="w-4 h-4" />, cost: 55 },
  { key: "karuzele", label: "Karuzele", icon: <LayoutGrid className="w-4 h-4" />, cost: 275 },
  { key: "stories", label: "Stories", icon: <Layers className="w-4 h-4" />, cost: 40 },
  { key: "video", label: "Reelsy AI", icon: <Film className="w-4 h-4" />, cost: 3100 },
  // Reklamy = tylko plany paid (lead gen / ecommerce), NIE Organic.
  {
    key: "reklamy",
    label: "Reklamy",
    icon: <Megaphone className="w-4 h-4" />,
    cost: 120,
    paidOnly: true,
  },
];

function clampTierToVertical(vertical: Vertical, wanted: Tier): Tier {
  const available = findVertical(vertical).tiers.map((t) => t.tier as Tier);
  if (available.includes(wanted)) return wanted;
  // najbliższy dostępny <= wanted, inaczej najwyższy dostępny
  const wantedIdx = TIER_ORDER.indexOf(wanted);
  for (let i = wantedIdx; i >= 0; i--) if (available.includes(TIER_ORDER[i])) return TIER_ORDER[i];
  return available[available.length - 1];
}

export default function PlanFinder() {
  const { toast } = useToast();
  const [goals, setGoals] = useState<Set<Vertical>>(new Set(["content_creation"]));
  const [needs, setNeeds] = useState<Set<string>>(new Set());
  const [annual, setAnnual] = useState(true);
  const [tokenBudget, setTokenBudget] = useState(900);
  const [loading, setLoading] = useState(false);

  const toggle = <T,>(set: Set<T>, key: T): Set<T> => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };

  const { vertical, tier, plan, extraGoals } = useMemo(() => {
    const selected = [...goals];
    const primary =
      selected.length > 0
        ? selected.sort(
            (a, b) =>
              GOALS.find((g) => g.key === b)!.priority - GOALS.find((g) => g.key === a)!.priority
          )[0]
        : ("content_creation" as Vertical);
    const wantedTier = NEEDS.reduce<Tier>((acc, n) => {
      if (!needs.has(n.key)) return acc;
      return TIER_ORDER.indexOf(n.tier) > TIER_ORDER.indexOf(acc) ? n.tier : acc;
    }, "start");
    const finalTier = clampTierToVertical(primary, wantedTier);
    const v = findVertical(primary);
    const p = v.tiers.find((t) => t.tier === finalTier) ?? v.tiers[0];
    const extras = selected.filter((g) => g !== primary);
    return { vertical: primary, tier: finalTier, plan: p, extraGoals: extras };
  }, [goals, needs]);

  const priceMonthly = plan.price;
  const priceShown = annual ? Math.round(plan.price * 0.8) : plan.price;
  const yearFull = priceMonthly * 12;
  const yearAnnual = Math.round(priceMonthly * 12 * 0.8);
  const verticalName = findVertical(vertical).name;
  const contentTypes = CONTENT_COSTS.filter(
    (c) => !(c.paidOnly && vertical === "content_creation")
  );
  const extraTokens = Math.max(0, tokenBudget - plan.tokens);
  const addonCost = topupCost(extraTokens);

  const startCheckout = async () => {
    setLoading(true);
    try {
      const data = (await apiRequest("POST", "/api/billing/create-checkout", {
        vertical,
        tier,
        cycle: annual ? "yearly" : "monthly",
      })) as { url?: string };
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("Brak URL Stripe Checkout");
    } catch (err) {
      toast({
        title: "Nie udało się otworzyć płatności",
        description: err instanceof Error ? err.message : "Spróbuj ponownie.",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  return (
    <div className="mb-10">
      <h2 className="text-2xl font-extrabold tracking-tight text-white mb-1">
        Znajdź plan dla siebie
      </h2>
      <p className="text-white/50 text-sm mb-6">
        Zaznacz co chcesz robić — dobierzemy plan i tokeny pod Ciebie
      </p>

      <div className="grid lg:grid-cols-[1fr_400px] gap-6 items-start">
        {/* LEFT */}
        <Card className="bg-card border-white/[0.08] p-6 space-y-7">
          <section>
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-6 h-6 rounded-full bg-white/[0.05] border border-white/10 grid place-items-center text-[11px] font-bold text-white/50">
                1
              </span>
              <h3 className="text-base font-bold text-white">Co chcesz robić?</h3>
              <span className="text-xs text-white/35">można kilka</span>
            </div>
            <div className="grid gap-2.5">
              {GOALS.map((g) => {
                const on = goals.has(g.key);
                const soon = findVertical(g.key).comingSoon;
                return (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => setGoals((s) => toggle(s, g.key))}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium text-left transition ${
                      on
                        ? "border-emerald-500 bg-emerald-500/[0.08] text-white"
                        : "border-white/[0.08] bg-white/[0.02] text-white/70 hover:border-white/20"
                    }`}
                  >
                    <span
                      className={`w-7 h-7 rounded-md grid place-items-center ${on ? "bg-emerald-500/20 text-emerald-400" : "bg-white/[0.05] text-white/40"}`}
                    >
                      {g.icon}
                    </span>
                    {g.label}
                    {soon && (
                      <span className="text-[10px] text-white/40 border border-white/15 rounded px-1.5 py-0.5">
                        wkrótce
                      </span>
                    )}
                    <span
                      className={`ml-auto w-5 h-5 rounded-md grid place-items-center text-[11px] ${on ? "bg-emerald-500 text-black" : "border border-white/15 text-transparent"}`}
                    >
                      <Check className="w-3 h-3" />
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="pt-6 border-t border-white/[0.06]">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-6 h-6 rounded-full bg-white/[0.05] border border-white/10 grid place-items-center text-[11px] font-bold text-white/50">
                2
              </span>
              <h3 className="text-base font-bold text-white">Czego potrzebujesz?</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {NEEDS.map((n) => {
                const on = needs.has(n.key);
                return (
                  <button
                    key={n.key}
                    type="button"
                    onClick={() => setNeeds((s) => toggle(s, n.key))}
                    className={`px-3.5 py-2 rounded-full text-[13px] border transition ${
                      on
                        ? "border-emerald-500 bg-emerald-500/[0.08] text-white"
                        : "border-white/[0.08] bg-white/[0.02] text-white/60 hover:border-white/20"
                    }`}
                  >
                    {n.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-white/35 mt-3">
              Plan Organic jest jednopoziomowy — te opcje dobierają tier tylko dla Reklam i
              E-commerce.
            </p>
          </section>

          <section className="pt-6 border-t border-white/[0.06]">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-6 h-6 rounded-full bg-white/[0.05] border border-white/10 grid place-items-center text-[11px] font-bold text-white/50">
                3
              </span>
              <h3 className="text-base font-bold text-white">Ile tokenów miesięcznie?</h3>
            </div>
            <input
              type="range"
              min={TOKEN_SLIDER.min}
              max={TOKEN_SLIDER.max}
              step={TOKEN_SLIDER.step}
              value={tokenBudget}
              onChange={(e) => setTokenBudget(Number(e.target.value))}
              className="w-full h-1.5 appearance-none rounded-full cursor-pointer"
              style={{
                background: `linear-gradient(to right, rgb(16 185 129) 0%, rgb(16 185 129) ${((tokenBudget - TOKEN_SLIDER.min) / (TOKEN_SLIDER.max - TOKEN_SLIDER.min)) * 100}%, rgba(255,255,255,0.1) ${((tokenBudget - TOKEN_SLIDER.min) / (TOKEN_SLIDER.max - TOKEN_SLIDER.min)) * 100}%, rgba(255,255,255,0.1) 100%)`,
              }}
            />
            <div className="flex items-baseline gap-2 mt-2.5 text-sm">
              <Coins className="w-4 h-4 text-emerald-400" />
              <b className="text-emerald-400 tabular-nums">{fmt(tokenBudget)}</b>
              <span className="text-white/60">tok/mies</span>
              <span className="ml-auto text-xs text-white/40">
                {tokenBudget > plan.tokens
                  ? `+${fmt(tokenBudget - plan.tokens)} z doładowań`
                  : `w planie ${plan.name}`}
              </span>
            </div>
            <p className="text-[11px] text-white/30 mt-3">
              Przesuń — po prawej zobaczysz ile treści z tego zrobisz.
            </p>
          </section>
        </Card>

        {/* RIGHT — recommendation */}
        <Card className="bg-card border-white/[0.08] p-6 lg:sticky lg:top-6 bg-gradient-to-b from-emerald-500/[0.05] to-transparent">
          <p className="text-center text-xs text-white/40 mb-3">Rekomendujemy</p>
          <div className="flex items-center gap-2.5">
            <span className="text-2xl font-extrabold tracking-tight text-white">
              {verticalName}
            </span>
            {plan.name !== verticalName && (
              <span className="text-sm font-bold text-emerald-400">{plan.name}</span>
            )}
            {annual && (
              <span className="bg-pink-600 text-white text-[11px] font-extrabold px-2 py-0.5 rounded-md">
                -20%
              </span>
            )}
          </div>
          <p className="text-white/50 text-[13px] mt-1 mb-4">{plan.subtitle}</p>

          <div className="flex items-center gap-4 bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3 mb-4 text-sm">
            <span className="flex items-center gap-1.5 font-bold text-white">
              <Coins className="w-4 h-4 text-emerald-400" />
              {fmt(plan.tokens)} tok/mies
            </span>
            <span className="flex items-center gap-1.5 text-white/60">
              <Users className="w-4 h-4" />
              {plan.users} {plan.users === 1 ? "użytkownik" : "użytkowników"}
            </span>
          </div>

          <p className="text-[11px] uppercase tracking-wide text-white/35 mb-2">
            Za {fmt(tokenBudget)} tokenów miesięcznie zrobisz
          </p>
          <div className="grid grid-cols-3 gap-1.5 mb-5">
            {contentTypes.map((c) => (
              <div
                key={c.key}
                className="rounded-lg bg-white/[0.02] border border-white/[0.06] px-2 py-2 text-center"
              >
                <div className="text-emerald-400/70 flex justify-center mb-1">{c.icon}</div>
                <div className="text-sm font-bold text-white tabular-nums leading-none">
                  ~{fmt(Math.floor(tokenBudget / c.cost))}
                </div>
                <div className="text-[10px] text-white/40 leading-tight mt-0.5">{c.label}</div>
              </div>
            ))}
          </div>

          <ul className="space-y-2 mb-5">
            {plan.features
              .filter((f) => f.included)
              .slice(0, 6)
              .map((f, i) => (
                <li key={i} className="flex gap-2 text-[13px] text-white/85">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  {f.text}
                </li>
              ))}
          </ul>

          {extraGoals.length > 0 && (
            <div className="flex items-start gap-2 mb-4 bg-emerald-500/[0.07] border border-emerald-500/25 rounded-lg px-3.5 py-2.5 text-[12.5px] text-white/80">
              <Sparkles className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              Potrzebujesz też: {extraGoals.map((g) => findVertical(g).name).join(", ")} — możesz
              dodać jako osobny plan (plany się sumują).
            </div>
          )}

          {extraTokens > 0 ? (
            <div className="mb-4">
              <div className="flex items-center gap-2 text-[13px] text-emerald-300 bg-emerald-500/[0.07] border border-emerald-500/25 rounded-lg px-3 py-2 mb-3">
                <Coins className="w-4 h-4 shrink-0" />
                Plan {fmt(priceShown)} zł + doładowanie {fmt(addonCost)} zł/mies ({fmt(extraTokens)}{" "}
                tok)
              </div>
              <div className="flex items-baseline gap-2.5">
                <span className="text-4xl font-extrabold tracking-tight text-white">
                  {fmt(priceShown + addonCost)} zł
                </span>
                <span className="text-white/50 text-[13px]">
                  /mies łącznie{annual ? " · plan płatny rocznie" : ""}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-baseline gap-2.5 mb-4">
              {annual && (
                <span className="text-pink-500 line-through text-lg font-bold">
                  {fmt(priceMonthly)} zł
                </span>
              )}
              <span className="text-4xl font-extrabold tracking-tight text-white">
                {fmt(priceShown)} zł
              </span>
              <span className="text-white/50 text-[13px]">
                {annual ? "/mies, płatne rocznie" : "/mies"}
              </span>
            </div>
          )}

          <Button
            onClick={startCheckout}
            disabled={loading}
            className="w-full h-12 text-[15px] font-extrabold bg-emerald-500 hover:bg-emerald-400 text-black"
          >
            {loading
              ? "Otwieram…"
              : `Wybierz ${verticalName}${plan.name !== verticalName ? " " + plan.name : ""}`}
          </Button>
          {annual && (
            <p className="text-center text-white/40 text-xs mt-3">
              <b className="text-white/60">Oszczędzasz {fmt(yearFull - yearAnnual)} zł</b> vs
              miesięcznie ({fmt(yearAnnual)} zł/rok)
            </p>
          )}

          <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-white/[0.06]">
            <button
              type="button"
              onClick={() => setAnnual((v) => !v)}
              className="flex items-center gap-2 text-[13px] text-white/60"
            >
              <span
                className={`w-9 h-5 rounded-full relative transition ${annual ? "bg-emerald-500" : "bg-white/15"}`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${annual ? "left-[18px]" : "left-0.5"}`}
                />
              </span>
              Rocznie{" "}
              <span className="bg-pink-600 text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded">
                -20%
              </span>
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
